import {
  ACCESS_LEVELS,
  RECORD_RIGHT_KEYS,
  SHAREABLE_ROLES,
  accessSummary,
  accessTabsFor,
  clampLevel,
  levelRank,
  minLevel,
  seesEveryContact,
  topLevel,
  type AccessLevel,
  type ContactTabDef,
  type MyContactAccess,
  type RecordRights,
} from '@ehr/shared';
import type { AccessPerson, ShareBody } from '../../lib/contactAccess.js';

/**
 * ehr#730 (SHARE_ACCESS_10-30-V11 lines 949-1180): the Share flow's state and rules, kept out of
 * the window so they can be tested: Who (users or roles, a role's members with people unticked),
 * Access per tab (Hidden / View / Edit per tab, All tabs, the three presets, record permissions,
 * never above the sharer's own access) and Review.
 */

export interface ShareTarget {
  id: string;
  name: string;
  category: string;
  ownerUserId: string | null;
}

/** The most the sharer may grant: tabs null = no tab limit (the owner, admins, managers). */
export interface ShareCap {
  tabs: Record<string, AccessLevel> | null;
  rights: RecordRights;
}

export type ShareChange =
  | { kind: 'user'; userId: string; tabs: Record<string, AccessLevel> | null; rights: RecordRights }
  | {
      kind: 'role';
      role: string;
      excluded: string[];
      tabs: Record<string, AccessLevel>;
      rights: RecordRights;
    };

export interface ShareDraft {
  step: number;
  whoTab: 'users' | 'roles';
  users: string[];
  /** role → the people unticked */
  roles: Record<string, string[]>;
  tabs: Record<string, AccessLevel>;
  rights: RecordRights;
  notify: boolean;
}

export type Preset = 'view' | 'edit' | 'full';

/** The cap from the sharer's own access on each contact (the lowest across them). */
export function capFrom(mines: MyContactAccess[]): ShareCap {
  let tabs: Record<string, AccessLevel> | null = null;
  const rights: RecordRights = { share: true, reassign: true, archive: true };
  for (const m of mines) {
    for (const k of RECORD_RIGHT_KEYS) if (!m.rights[k]) rights[k] = false;
    if (m.admin || m.owner || m.unrestricted) continue;
    const t: Record<string, AccessLevel> = { ...(tabs ?? m.tabs) };
    if (tabs) for (const k of Object.keys(t)) t[k] = minLevel(t[k], m.tabs[k] ?? 'hidden');
    tabs = t;
  }
  return { tabs, rights };
}

/** The cap for someone who will own the contact they're creating (New Contact). */
export function creatorCap(role: string | undefined): ShareCap {
  return {
    tabs: null,
    rights: seesEveryContact(role)
      ? { share: true, reassign: true, archive: true }
      : { share: true, reassign: false, archive: false },
  };
}

export function capLevel(cap: ShareCap, t: ContactTabDef): AccessLevel {
  return cap.tabs ? (cap.tabs[t.key] ?? 'hidden') : topLevel(t);
}

/** The levels a tab's row offers (V11: the Profile has no Hidden, Messages has no Edit). */
export function rowLevels(t: ContactTabDef): AccessLevel[] {
  return ACCESS_LEVELS.filter((l) => clampLevel(t, l) === l);
}

export function shareTabs(targets: ShareTarget[]): ContactTabDef[] {
  return accessTabsFor(targets.map((t) => t.category));
}

function fit(t: ContactTabDef, level: AccessLevel, cap: ShareCap): AccessLevel {
  return minLevel(clampLevel(t, level), capLevel(cap, t));
}

export function shareSteps(change: ShareChange | null | undefined): string[] {
  return change?.kind === 'user'
    ? ['Access per tab', 'Review']
    : ['Who', 'Access per tab', 'Review'];
}

/** V11 openShare: every tab at its top level (within the cap), no permissions, Notify them on. */
export function initialDraft(
  targets: ShareTarget[],
  cap: ShareCap,
  change?: ShareChange | null,
  start?: Partial<ShareDraft> | null,
): ShareDraft {
  const tabs: Record<string, AccessLevel> = {};
  for (const t of shareTabs(targets)) {
    const from = change
      ? change.tabs
        ? (change.tabs[t.key] ?? 'hidden')
        : topLevel(t)
      : topLevel(t);
    tabs[t.key] = fit(t, from, cap);
  }
  const d: ShareDraft = {
    step: 0,
    whoTab: change?.kind === 'role' ? 'roles' : 'users',
    users: change?.kind === 'user' ? [change.userId] : [],
    roles: change?.kind === 'role' ? { [change.role]: [...change.excluded] } : {},
    tabs,
    rights: change ? { ...change.rights } : { share: false, reassign: false, archive: false },
    notify: true,
  };
  return start ? { ...d, ...start, step: 0 } : d;
}

export function setTabLevel(
  d: ShareDraft,
  t: ContactTabDef,
  level: AccessLevel,
  cap: ShareCap,
): ShareDraft {
  return { ...d, tabs: { ...d.tabs, [t.key]: fit(t, level, cap) } };
}

export function setAllLevels(
  d: ShareDraft,
  targets: ShareTarget[],
  level: AccessLevel,
  cap: ShareCap,
): ShareDraft {
  const tabs = { ...d.tabs };
  for (const t of shareTabs(targets)) tabs[t.key] = fit(t, level, cap);
  return { ...d, tabs };
}

/** View only / Edit all (no permissions) / Full Access (Edit everywhere + every permission allowed). */
export function applyPreset(
  d: ShareDraft,
  targets: ShareTarget[],
  p: Preset,
  cap: ShareCap,
): ShareDraft {
  const next = setAllLevels(d, targets, p === 'view' ? 'view' : 'edit', cap);
  const rights = { share: false, reassign: false, archive: false };
  if (p === 'full') for (const k of RECORD_RIGHT_KEYS) rights[k] = cap.rights[k];
  return { ...next, rights };
}

export function toggleRight(d: ShareDraft, k: keyof RecordRights, cap: ShareCap): ShareDraft {
  if (!cap.rights[k]) return d;
  return { ...d, rights: { ...d.rights, [k]: !d.rights[k] } };
}

/** The All tabs row's level when every unlocked tab agrees, else null. */
export function uniformLevel(d: ShareDraft, targets: ShareTarget[]): AccessLevel | null {
  const levels = shareTabs(targets)
    .filter((t) => !t.lockMin && !t.lockMax)
    .map((t) => d.tabs[t.key]);
  return levels.length && levels.every((l) => l === levels[0]) ? (levels[0] ?? null) : null;
}

export function toggleUser(d: ShareDraft, id: string): ShareDraft {
  return {
    ...d,
    users: d.users.includes(id) ? d.users.filter((u) => u !== id) : [...d.users, id],
  };
}

export function toggleRole(d: ShareDraft, role: string): ShareDraft {
  const roles = { ...d.roles };
  if (roles[role]) delete roles[role];
  else roles[role] = [];
  return { ...d, roles };
}

export function toggleMember(d: ShareDraft, role: string, userId: string): ShareDraft {
  const ex = d.roles[role];
  if (!ex) return d;
  return {
    ...d,
    roles: {
      ...d.roles,
      [role]: ex.includes(userId) ? ex.filter((x) => x !== userId) : [...ex, userId],
    },
  };
}

export function setAllMembers(
  d: ShareDraft,
  role: string,
  on: boolean,
  people: AccessPerson[],
): ShareDraft {
  if (!d.roles[role]) return d;
  return {
    ...d,
    roles: { ...d.roles, [role]: on ? [] : people.filter((p) => p.role === role).map((p) => p.id) },
  };
}

export function ownerIds(targets: ShareTarget[]): Set<string> {
  return new Set(targets.map((t) => t.ownerUserId).filter((x): x is string => !!x));
}

/** Who the Users list offers: not the sharer, not an owner, not admins / managers (they see all). */
export function pickableUsers(
  people: AccessPerson[],
  targets: ShareTarget[],
  meId: string | undefined,
): AccessPerson[] {
  const owners = ownerIds(targets);
  return people.filter((p) => p.id !== meId && !owners.has(p.id) && !seesEveryContact(p.role));
}

export function roleMembers(people: AccessPerson[], role: string): AccessPerson[] {
  return people.filter((p) => p.role === role);
}

/** A role's members the grant reaches: not unticked, not an owner. */
export function includedMembers(
  d: ShareDraft,
  role: string,
  people: AccessPerson[],
  targets: ShareTarget[],
): AccessPerson[] {
  const ex = new Set(d.roles[role] ?? []);
  const owners = ownerIds(targets);
  return roleMembers(people, role).filter((p) => !ex.has(p.id) && !owners.has(p.id));
}

export function eligibleMembers(people: AccessPerson[], role: string, targets: ShareTarget[]) {
  const owners = ownerIds(targets);
  return roleMembers(people, role).filter((p) => !owners.has(p.id));
}

export function recipientCount(d: ShareDraft, people: AccessPerson[], targets: ShareTarget[]) {
  let n = d.users.length;
  for (const r of Object.keys(d.roles)) n += includedMembers(d, r, people, targets).length;
  return n;
}

/** Next on Who: someone to share with (a role with everyone unticked counts as no one). */
export function whoProblem(d: ShareDraft, people: AccessPerson[], targets: ShareTarget[]) {
  return recipientCount(d, people, targets) === 0 ? 'Pick at least one user or role' : null;
}

export function shareBody(
  d: ShareDraft,
  targets: ShareTarget[],
  change: ShareChange | null | undefined,
): ShareBody {
  return {
    contactIds: targets.map((t) => t.id),
    users: [...d.users],
    roles: Object.entries(d.roles)
      .filter(([r]) => (SHAREABLE_ROLES as readonly string[]).includes(r))
      .map(([role, excluded]) => ({ role: role as ShareBody['roles'][number]['role'], excluded })),
    tabs: { ...d.tabs } as ShareBody['tabs'],
    rights: { ...d.rights },
    notify: d.notify,
    change: !!change,
  };
}

/** The Review row's Access words for the first contact (V11 renderReview). */
export function reviewSummary(d: ShareDraft, targets: ShareTarget[]): string {
  const cat = targets[0]?.category ?? 'client';
  return accessSummary(cat, d.tabs, d.rights);
}

/** The saved message: "Shared with Dana and Leo", "Shared with 5 people", or "Access updated". */
export function savedMessage(
  d: ShareDraft,
  people: AccessPerson[],
  targets: ShareTarget[],
  change: ShareChange | null | undefined,
  roleLabel: (r: string) => string,
): string {
  if (change) return 'Access updated';
  const names = [
    ...d.users.map((id) => people.find((p) => p.id === id)?.name ?? 'someone'),
    ...Object.keys(d.roles).map((r) => `role ${roleLabel(r)}`),
  ];
  if (names.length <= 2) return `Shared with ${names.join(' and ')}`;
  const n = recipientCount(d, people, targets);
  return `Shared with ${n} ${n === 1 ? 'person' : 'people'}`;
}

/** A level the cap stops: its button is greyed with V11's tooltip. */
export function aboveCap(cap: ShareCap, t: ContactTabDef, l: AccessLevel): boolean {
  return levelRank(l) > levelRank(capLevel(cap, t));
}
